import { useRef, useState, type JSX } from 'react';

import { shaktiApi, ApiError } from '../api/client';
import { useProjectScopedEffect } from '../stores/project';
import type { AgentActivityEntry, ActivitySeverity } from '../api/types';

/**
 * Agent Activity Feed (FEAT-U06 / FEAT-ADD-016).
 *
 * Infinite-scroll paginated feed with agent / severity filters. The WS
 * `agent-activity` channel deposits live events into the store; the REST
 * endpoint provides the cursor-paginated backlog.
 */
export function ActivityRoute(): JSX.Element {
  const [entries, setEntries] = useState<AgentActivityEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [endOfFeed, setEndOfFeed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState<ActivitySeverity | ''>('');
  const loadedOnce = useRef(false);

  // N5.5 / W1.1 (v1.1) — project-scoped; filter changes and project
  // switches both reset the feed and fire a fresh backlog fetch while
  // aborting any in-flight prior-project response.
  useProjectScopedEffect(
    (signal) => {
      loadedOnce.current = false;
      setEntries([]);
      setCursor(null);
      setEndOfFeed(false);
      void fetchMore(null, agentFilter, severityFilter, signal);
    },
    [agentFilter, severityFilter],
  );

  const fetchMore = async (
    fromCursor: string | null,
    agent: string,
    severity: ActivitySeverity | '',
    signal?: AbortSignal,
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const page = await shaktiApi.listAgentActivity({
        limit: 50,
        ...(fromCursor ? { cursor: fromCursor } : {}),
        ...(agent ? { agent } : {}),
        ...(severity ? { severity } : {}),
        ...(signal ? { signal } : {}),
      });
      if (signal?.aborted) return;
      setEntries((prev) => [...prev, ...page.entries]);
      setCursor(page.next_cursor ?? null);
      if (!page.next_cursor || page.entries.length === 0) {
        setEndOfFeed(true);
      }
      loadedOnce.current = true;
    } catch (e) {
      if (signal?.aborted) return;
      setError(e instanceof ApiError ? e.body?.error ?? e.message : 'Failed to load activity');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  return (
    <section aria-labelledby="act-title" className="mx-auto flex max-w-5xl flex-col gap-4">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/activity
        </span>
        <h1 id="act-title" className="text-2xl font-semibold text-fg-primary">
          Agent Activity Feed
        </h1>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-fg-muted">
          Agent
          <input
            type="text"
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            placeholder="filter by agent name…"
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col text-xs text-fg-muted">
          Severity
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as ActivitySeverity | '')}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
          >
            <option value="">(any)</option>
            <option value="debug">debug</option>
            <option value="info">info</option>
            <option value="warn">warn</option>
            <option value="error">error</option>
          </select>
        </label>
      </div>

      {error ? (
        <div role="alert" className="shakti-card border border-status-error/40 text-status-error">
          {error}
        </div>
      ) : null}

      <div className="shakti-card">
        {entries.length === 0 && !loading ? (
          <p className="text-sm text-fg-muted">No activity yet for the current filters.</p>
        ) : (
          <ul className="flex flex-col gap-1" aria-label="Activity entries">
            {entries.map((e) => (
              <AgentRow key={e.activity_id} entry={e} />
            ))}
          </ul>
        )}
        {!endOfFeed ? (
          <button
            type="button"
            onClick={() => fetchMore(cursor, agentFilter, severityFilter)}
            disabled={loading}
            className="mt-3 rounded-sm border border-border-subtle px-3 py-1 text-xs text-fg-secondary hover:border-border-strong disabled:opacity-40"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        ) : loadedOnce.current ? (
          <p className="mt-3 text-2xs text-fg-muted">End of feed.</p>
        ) : null}
      </div>
    </section>
  );
}

function AgentRow({ entry }: { entry: AgentActivityEntry }): JSX.Element {
  return (
    <li
      className={`rounded-sm border-l-2 px-3 py-2 text-xs font-mono ${severityTone(entry.severity)}`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-fg-muted">{new Date(entry.emitted_at).toLocaleTimeString()}</span>
        <span className="font-semibold text-fg-primary">{entry.agent}</span>
        {entry.tool ? <span className="text-fg-muted">[{entry.tool}]</span> : null}
        <span className="text-2xs uppercase">{entry.severity}</span>
        {/*
          M1.4 (round-5) — agent cost streaming. When the backend attaches
          tokens_in / cost_usd to a completion event the dashboard
          surfaces it inline so operators see real-time BYOK spend per
          agent invocation without opening /dashboard/costs.
        */}
        {entry.cost_usd !== undefined ? (
          <span
            className="rounded-sm bg-accent-muted px-2 py-0.5 text-2xs font-semibold text-accent"
            title={`${entry.tokens_in ?? 0} in / ${entry.tokens_out ?? 0} out tokens`}
          >
            ${entry.cost_usd.toFixed(4)}
          </span>
        ) : null}
      </div>
      <p className="mt-1 whitespace-pre-wrap text-fg-secondary">{entry.message}</p>
    </li>
  );
}

function severityTone(severity: ActivitySeverity): string {
  switch (severity) {
    case 'error':
      return 'border-status-error bg-status-error/5';
    case 'warn':
      return 'border-status-warning bg-status-warning/5';
    case 'info':
      return 'border-status-info bg-status-info/5';
    case 'debug':
      return 'border-border-subtle bg-bg-raised/40';
  }
}
